import { useGetNotificationByAcl } from 'api-hooks/notification/query';
import { RefundLiteModel, useGetRefunds } from 'api-hooks/refund/refund';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { string2money } from 'common/utils/string';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { IColumn } from 'components/elements/table-component/table-component';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import useStatusFilter from 'hooks/use-status-filter';
import { Container, FullContainer, TopContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import RefundTable from './components/table';
import RefundListTabs from '../components/refund-list-tabs';
import RefundPillStatus from '../components/refund-pill-status';
import ReleasePillStatus from '../components/release-pill-status';

export default function RefundList() {
  const { t } = useTranslation();
  const { queryStatus, queryPage, queryTab } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const [activeKey, setActiveKey] = React.useState<React.Key>(queryTab);
  const [status, setStatus] = useStatusFilter<string | undefined>(
    queryStatus,
    setPage,
  );

  const formatDate = useDate();

  const _columns = React.useMemo<IColumn<RefundLiteModel>[]>(
    () => [
      {
        header: t('common:status'),
        accessorKey: 'status',
        minSize: 180,
        cell: ({ getValue }) => (
          <RefundPillStatus value={getValue() as string} />
        ),
      },
      {
        header: t('refund:requested_at'),
        accessorKey: 'requestedAt',
        minSize: 100,
        sortName: 'requested_at',
        cell: ({ getValue }) => {
          return <>{formatDate(getValue() as Date, 'dd MMM yyyy')}</>;
        },
      },
      {
        header: t('common:number'),
        accessorKey: 'number',
        minSize: 160,
      },
      {
        header: t('common:invoice_number'),
        accessorKey: 'invoiceNumber',
        minSize: 300,
        sortName: 'invoice_number',
        cell: ({ row }) => {
          const { original } = row;
          return (
            <FullContainer
              $style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {original.invoiceNumber}
              <ReleasePillStatus value={original.releaseCustomerStatus} />
            </FullContainer>
          );
        },
      },
      {
        header: t('common:customer'),
        accessorKey: 'customer',
        minSize: 150,
        sortName: 'customer',
      },
      {
        header: t('refund:customer_refund'),
        accessorKey: 'customerRefund',
        minSize: 150,
        textAlign: 'right',
        cell: ({ getValue }) => string2money(getValue() as number),
      },
      {
        header: t('common:order_number'),
        accessorKey: 'orderNumber',
        minSize: 300,
        cell: ({ row }) => {
          const { original } = row;

          if (original.orderNumber) {
            return (
              <FullContainer
                $style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                {original.orderNumber}
                <ReleasePillStatus value={original.releaseAgentStatus} />
              </FullContainer>
            );
          } else {
            return <>-</>;
          }
        },
      },
      {
        header: t('common:agent'),
        accessorKey: 'agent',
        minSize: 150,
        cell: ({ getValue }) => {
          return getValue() ?? '-';
        },
      },
      {
        header: t('refund:agent_refund'),
        accessorKey: 'agentRefund',
        minSize: 150,
        textAlign: 'right',
        cell: ({ getValue }) => string2money(getValue() as number),
      },
      {
        header: t('refund:refund_reason'),
        accessorKey: 'reason',
        minSize: 250,
        sortName: 'reason',
      },
      {
        header: t('common:created_by'),
        accessorKey: 'createdBy.name',
        minSize: 150,
        sortName: 'created_by',
        cell: ({ getValue }) => getValue(),
      },
    ],
    [formatDate, t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetRefunds,
    {
      params: {
        page,
        filter: { status },
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  const { data: dataNotification, refetch: refetchNotification } =
    useGetNotificationByAcl({
      acl: AuthorizationRules.RefundsPage,
    });

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);
  const permission = useGetCrudPermission('refunds');
  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:refund')}
          showAdd={permission.create}
          onRefresh={() => {
            refetch();
            refetchNotification();
          }}
          linkAdd={NavigationRoutes.RefundCreate}
        />

        <RefundListTabs
          {...{
            activeKey,
            setActiveKey: (value) => {
              setActiveKey(value);
              refetchNotification();
              setFilters();
            },
            setStatus,
            badge: dataNotification?.data,
          }}
        />

        <TopContainer>
          <QueryFilter filters={filters} />
        </TopContainer>
        <RefundTable
          refunds={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
